/**
 * data/repos/platformRepo — repositório das áreas `businessUnits`, `branding`, `tenants` (DD-004).
 *
 * Resolve a fonte via `data/source.ts`:
 * - businessUnits real → GET /v1/business-units
 * - branding real      → GET /v1/tenant/branding  |  PUT /v1/tenant/branding
 * - tenants real       → GET /v1/tenant  |  PATCH /v1/tenant
 * - platform tenants   → POST /v1/platform/tenants, POST .../suspend, .../reactivate
 *
 * Mapeamento honesto: apenas campos presentes no contrato são propagados.
 * Campos presentes no mock mas ausentes no backend são marcados como opcionais (`?`)
 * e recebem fallback "—" na UI (conforme padrão do projeto).
 *
 * Referências:
 *   - Organization.Api / BusinessUnitsController
 *       GET    /api/v1/business-units           → BusinessUnitResult[] {id, name, active}
 *       POST   /api/v1/business-units           → CreateBusinessUnitResponse {id, name, active}
 *       PUT    /api/v1/business-units/{id}      → 200 vazio (rename, body: {name})
 *       DELETE /api/v1/business-units/{id}      → 204 (soft-delete/deactivate)
 *   - Organization.Api / StagesController (pipeline comercial)
 *       GET    /api/v1/business-units/{buId}/stages        → StageResult[]
 *       POST   /api/v1/business-units/{buId}/stages        → 201 Created
 *       PUT    /api/v1/business-units/{buId}/stages/{id}   → 200 (reorder completo, body: {newPositions})
 *       DELETE /api/v1/business-units/{buId}/stages/{id}   → 204
 *   - Organization.Api / PartnershipStagesController (pipeline de parcerias)
 *       GET    /api/v1/business-units/{buId}/partnership-stages        → StageResult[]
 *       POST   /api/v1/business-units/{buId}/partnership-stages        → 201 Created
 *       PUT    /api/v1/business-units/{buId}/partnership-stages/{id}   → 200
 *       PUT    /api/v1/business-units/{buId}/partnership-stages/reorder → 200 (body: {newPositions})
 *       DELETE /api/v1/business-units/{buId}/partnership-stages/{id}   → 204
 *   - TenantAdministration.Api / TenantController
 *       GET   /api/v1/tenant           → TenantResponse
 *       PATCH /api/v1/tenant           → TenantResponse
 *       GET   /api/v1/tenant/branding  → BrandingResponse
 *       PUT   /api/v1/tenant/branding  → BrandingUpdateResponse (multipart: primaryColor, secondaryColor, logo?)
 *   - TenantAdministration.Api / PlatformTenantController
 *       POST /api/v1/platform/tenants, POST .../suspend, POST .../reactivate
 *
 * Bugs conhecidos do backend (confirmados via curl em 2026-06-17):
 *   - GET /v1/business-units/{buId}/stages retorna ORG-ERR-016: handler [AllowAnonymous]
 *     não popula ITenantContext → GetByIdAsync aplica RLS e não localiza a BU.
 *     A UI trata graciosamente exibindo lista vazia com aviso de indisponibilidade.
 *     Nota: GET de partnership-stages NÃO tem esse bug — é autenticado normalmente.
 *   - BUs criadas via POST não aparecem nas listagens subsequentes (tenant_id não
 *     associado corretamente no banco). PUT/DELETE também retornam 404 para BUs novas.
 *     A UI invalida a query após cada mutação para recarregar o estado real.
 *   - PUT /v1/tenant/branding intermitentemente retorna 500. A UI trata como erro
 *     e exibe mensagem ao usuário.
 */

import { apiClient, postMultipart } from "../../services/ApiClient.js";
import { resolveSource } from "../source.js";

// ---------------------------------------------------------------------------
// DTOs do backend
// ---------------------------------------------------------------------------

/** BusinessUnit do Organization service. GET /api/v1/business-units */
interface BusinessUnitApiDto {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
}

/** Resposta de criação de BU. POST /api/v1/business-units → 201 */
interface CreateBusinessUnitApiDto {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
}

/**
 * Estágio de pipeline do Organization service.
 * GET /api/v1/business-units/{buId}/stages → StageResult[]
 * POST /api/v1/business-units/{buId}/stages body: AddStageRequest
 */
interface StageApiDto {
  readonly id: string;
  readonly name: string;
  readonly probabilityPercent: number;
  readonly category: string;
  readonly position: number;
}

/** DTO do tenant corrente. GET /api/v1/tenant → TenantResponse */
interface TenantApiDto {
  readonly tenantId: string;
  readonly slug: string;
  readonly displayName: string;
  readonly timezone: string;
  readonly digestTime: string;
  readonly status: string;
}

/** DTO do branding do tenant corrente. GET /api/v1/tenant/branding */
interface BrandingApiDto {
  readonly tenantId: string;
  readonly logoUrl?: string | null;
  readonly faviconUrl?: string | null;
  readonly colors?: {
    readonly primary: string;
    readonly secondary: string;
  } | null;
  readonly wcagContrastOk: boolean;
  readonly lastContrastRatio: number;
  readonly derivedTones?: {
    readonly primaryHover: string;
    readonly primaryActive: string;
    readonly primaryMuted: string;
    readonly secondaryHover: string;
    readonly secondaryActive: string;
    readonly secondaryMuted: string;
  } | null;
  readonly updatedAt: string;
}

/**
 * DTO do brand.json público. GET /brand/{slug}/brand.json — rota anônima FORA de /v1
 * (mesma superfície pública que a CDN cacheia), diferente de BrandingApiDto acima
 * (GET /v1/tenant/branding, exige RequiresTenantAdmin).
 */
interface PublicBrandJsonApiDto {
  readonly slug: string;
  readonly logoUrl?: string | null;
  readonly faviconUrl?: string | null;
  readonly colors?: {
    readonly primary: string;
    readonly secondary: string;
  } | null;
  readonly wcagContrastOk: boolean;
  readonly version: string;
}

/**
 * Resposta do PUT /v1/tenant/branding.
 * Retorna os tons derivados e resultado do contraste, sem o BrandingResponse completo.
 */
interface BrandingUpdateApiDto {
  readonly wcagContrastOk: boolean;
  readonly contrastRatio: number;
  readonly logoUrl: string | null;
  readonly faviconUrl: string | null;
}

/** DTO de resposta de provisionamento. POST /api/v1/platform/tenants */
interface ProvisionTenantApiDto {
  readonly tenantId: string;
  readonly slug: string;
  readonly status: string;
}

/** DTO de operação de estado (suspend/reactivate). */
interface TenantStateApiDto {
  readonly tenantId: string;
  readonly status: string;
}

// ---------------------------------------------------------------------------
// Tipos de domínio do portal
// ---------------------------------------------------------------------------

/**
 * Business Unit normalizada para o portal.
 *
 * Campos garantidos pelo backend: id, name, active.
 * Campos opcionais (ausentes no backend, presentes no mock): slug, members, etc.
 */
export interface PortalBusinessUnit {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
  // Campos ausentes no contrato atual do backend — opcionais para tolerância
  readonly short?: string;
  readonly slug?: string;
  readonly tone?: string;
  readonly members?: number;
  readonly accounts?: number;
  readonly created?: string;
}

/** Parâmetros para criação de Business Unit. POST /v1/business-units */
export interface CreateBusinessUnitInput {
  readonly name: string;
}

/** Parâmetros para renomear Business Unit. PUT /v1/business-units/{id} */
export interface RenameBusinessUnitInput {
  readonly id: string;
  readonly name: string;
}

/**
 * Estágio de pipeline normalizado para o portal.
 * Reflete StageResult do Organization service.
 */
export interface PortalStage {
  readonly id: string;
  readonly name: string;
  readonly probabilityPercent: number;
  /** Categoria: "open" | "won" | "lost" */
  readonly category: string;
  readonly position: number;
}

/** Parâmetros para adição de estágio. POST /v1/business-units/{buId}/stages */
export interface AddStageInput {
  readonly name: string;
  readonly probabilityPercent: number;
  readonly category: "open" | "won" | "lost";
  readonly position: number;
}

/**
 * Parâmetros para reordenação de estágios.
 * PUT /v1/business-units/{buId}/stages/{id}
 * Body: { newPositions: Record<stageId, position> }
 */
export interface ReorderStagesInput {
  /** id arbitrário de um estágio (ignorado pelo backend; reorder é sempre completo) */
  readonly stageId: string;
  /** Mapa stageId → nova posição (base 1) */
  readonly newPositions: Record<string, number>;
}

/**
 * Parâmetros para edição de atributos de estágio.
 * PUT /v1/business-units/{buId}/stages/{id}
 */
export interface UpdateStageInput {
  readonly stageId: string;
  readonly name: string;
  readonly probabilityPercent: number;
  readonly category: "open" | "won" | "lost";
}

/** Tenant corrente normalizado para o portal. */
export interface PortalTenant {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly timezone: string;
  readonly digestTime: string;
  readonly status: string;
}

/** Branding do tenant normalizado para o portal. */
export interface PortalBranding {
  readonly tenantId: string;
  readonly logoUrl: string | null;
  readonly faviconUrl: string | null;
  readonly primaryColor: string | null;
  readonly secondaryColor: string | null;
  readonly wcagContrastOk: boolean;
  readonly lastContrastRatio: number;
  readonly updatedAt: string;
}

/**
 * Branding público (anônimo) — subconjunto de PortalBranding devolvido por brand.json,
 * sem os campos que exigem sessão de TenantAdmin (wcagContrastOk/lastContrastRatio/updatedAt
 * fazem sentido no formulário de edição, não no slot de identidade que todo usuário vê).
 * Usado pelos slots de identidade do tenant que TODO usuário autenticado precisa ver
 * (sidebar, seletor de tenant, cores globais) — ver achado da revisão adversarial: alimentar
 * esses slots com getBranding() (RequiresTenantAdmin) deixava o logo/cores do tenant invisíveis
 * para qualquer papel que não fosse TenantAdmin, que é a maioria dos usuários do produto.
 */
export interface PortalPublicBranding {
  readonly logoUrl: string | null;
  readonly faviconUrl: string | null;
  readonly primaryColor: string | null;
  readonly secondaryColor: string | null;
}

/**
 * Parâmetros para atualização de branding via PUT /v1/tenant/branding (multipart).
 * O campo `logoFile` substitui o campo `logoUrl` (input type=file). `faviconFile` é o
 * equivalente para o ícone do navegador (REQ-13, REQ-16, DD-013) — anexado ao MESMO PUT
 * (campo "favicon"), diferente do logo, que tem endpoint dedicado (POST .../branding/logo).
 */
export interface UpdateBrandingInput {
  readonly primaryColor: string;
  readonly secondaryColor: string;
  readonly logoFile?: File | undefined;
  /** Mantido por compatibilidade com callers antigos — ignorado se logoFile presente. */
  readonly logoUrl?: string | undefined;
  readonly faviconFile?: File | undefined;
  /** Mantido por compatibilidade com callers antigos — ignorado se faviconFile presente. */
  readonly faviconUrl?: string | undefined;
}

/** Parâmetros para atualização do tenant via PATCH /v1/tenant. */
export interface UpdateTenantInput {
  readonly timezone?: string;
  readonly digestTime?: string;
}

/** Parâmetros para provisionamento de tenant. */
export interface ProvisionTenantInput {
  readonly slug: string;
  readonly slugConfirmation: string;
  readonly displayName: string;
  readonly timezone: string;
  readonly digestTime: string;
  readonly adminEmail: string;
}

/** Resultado de provisionamento de tenant. */
export interface ProvisionTenantResult {
  readonly tenantId: string;
  readonly slug: string;
  readonly status: string;
}

/** Resultado de operação de estado (suspend/reactivate). */
export interface TenantStateResult {
  readonly tenantId: string;
  readonly status: string;
}

// ---------------------------------------------------------------------------
// Mapeamentos DTO → portal
// ---------------------------------------------------------------------------

function mapApiBusinessUnit(dto: BusinessUnitApiDto): PortalBusinessUnit {
  return {
    id: dto.id,
    name: dto.name,
    active: dto.active,
  };
}

function mapApiStage(dto: StageApiDto): PortalStage {
  return {
    id: dto.id,
    name: dto.name,
    probabilityPercent: dto.probabilityPercent,
    category: dto.category,
    position: dto.position,
  };
}

function mapApiTenant(dto: TenantApiDto): PortalTenant {
  return {
    id: dto.tenantId,
    slug: dto.slug,
    displayName: dto.displayName,
    timezone: dto.timezone,
    digestTime: dto.digestTime,
    status: dto.status,
  };
}

function mapApiBranding(dto: BrandingApiDto): PortalBranding {
  return {
    tenantId: dto.tenantId,
    logoUrl: dto.logoUrl ?? null,
    faviconUrl: dto.faviconUrl ?? null,
    primaryColor: dto.colors?.primary ?? null,
    secondaryColor: dto.colors?.secondary ?? null,
    wcagContrastOk: dto.wcagContrastOk,
    lastContrastRatio: dto.lastContrastRatio,
    updatedAt: dto.updatedAt,
  };
}

// ---------------------------------------------------------------------------
// Mapeamentos mock → portal
// ---------------------------------------------------------------------------

function mapMockBusinessUnit(bu: {
  id: string; name: string; short: string; slug: string;
  tone: string; members: number; accounts: number;
  created: string; status: string;
}): PortalBusinessUnit {
  return {
    id: bu.id,
    name: bu.name,
    active: bu.status === "active",
    short: bu.short,
    slug: bu.slug,
    tone: bu.tone,
    members: bu.members,
    accounts: bu.accounts,
    created: bu.created,
  };
}

// ---------------------------------------------------------------------------
// Business Units — listagem
// ---------------------------------------------------------------------------

/**
 * Lista as Business Units ativas do tenant.
 * Área `businessUnits` real → GET /v1/business-units
 */
export async function listBusinessUnits(): Promise<readonly PortalBusinessUnit[]> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    const dtos = await apiClient.get<BusinessUnitApiDto[]>("/business-units");
    return dtos.map(mapApiBusinessUnit);
  }

  if (!source.mock) throw new Error(`[repo] mock não disponível para área: ${source.area}`);
  const mock = source.mock;
  return mock.bus.map(mapMockBusinessUnit);
}

// ---------------------------------------------------------------------------
// Business Units — criação
// ---------------------------------------------------------------------------

/**
 * Cria uma nova Business Unit no tenant.
 * POST /v1/business-units → 201 {id, name, active}
 *
 * Nota: existe bug no backend onde BUs novas não aparecem na listagem subsequente
 * (tenant_id não associado corretamente). A UI invalida a query após a criação
 * para recarregar o estado real e exibe feedback honesto ao usuário.
 */
export async function createBusinessUnit(input: CreateBusinessUnitInput): Promise<PortalBusinessUnit> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    const dto = await apiClient.post<CreateBusinessUnitApiDto>("/business-units", {
      name: input.name,
    });
    return mapApiBusinessUnit(dto);
  }

  // Mock: retorna BU simulada sem persistir
  return {
    id: `mock-bu-${Date.now()}`,
    name: input.name,
    active: true,
  };
}

// ---------------------------------------------------------------------------
// Business Units — renomear
// ---------------------------------------------------------------------------

/**
 * Renomeia uma Business Unit.
 * PUT /v1/business-units/{id} → 200 vazio
 * Body: { name: string }
 */
export async function renameBusinessUnit(input: RenameBusinessUnitInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.put<void>(`/business-units/${input.id}`, { name: input.name });
    return;
  }

  // Mock: no-op (sem persistência)
}

// ---------------------------------------------------------------------------
// Business Units — desativar
// ---------------------------------------------------------------------------

/**
 * Desativa (soft-delete) uma Business Unit.
 * POST /v1/business-units/{id}/deactivate → 204
 *
 * Sempre disponível quando não há oportunidades ativas (ORG-ERR-002 caso contrário),
 * inclusive para BUs com histórico de oportunidades fechadas.
 */
export async function deactivateBusinessUnit(id: string): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.post<void>(`/business-units/${id}/deactivate`, {});
    return;
  }

  // Mock: no-op
}

/**
 * Remove definitivamente (hard delete) uma Business Unit.
 * DELETE /v1/business-units/{id} → 204
 *
 * Bloqueada quando a BU possui oportunidades fechadas (ORG-ERR-018) — nesse caso
 * apenas a desativação é permitida; ou ativas (ORG-ERR-002). O caller deve tratar
 * o 409 e oferecer a desativação como alternativa.
 */
export async function removeBusinessUnit(id: string): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.delete<void>(`/business-units/${id}`);
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Stages — listagem por BU
// ---------------------------------------------------------------------------

/**
 * Lista os estágios de pipeline de uma BU, ordenados por posição.
 * GET /v1/business-units/{buId}/stages → StageResult[]
 *
 * Nota: este endpoint retorna ORG-ERR-016 (bug no backend: AllowAnonymous remove
 * o ITenantContext e o GetByIdAsync falha com RLS). A UI trata graciosamente.
 */
export async function listStages(buId: string): Promise<readonly PortalStage[]> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    const dtos = await apiClient.get<StageApiDto[]>(`/business-units/${buId}/stages`);
    return dtos.map(mapApiStage);
  }

  // Mock: retorna estágios padrão para desenvolvimento
  return [
    { id: "mock-s1", name: "Prospecção", probabilityPercent: 10, category: "open", position: 1 },
    { id: "mock-s2", name: "Qualificação", probabilityPercent: 25, category: "open", position: 2 },
    { id: "mock-s3", name: "Proposta", probabilityPercent: 50, category: "open", position: 3 },
    { id: "mock-s4", name: "Negociação", probabilityPercent: 75, category: "open", position: 4 },
    { id: "mock-s5", name: "Fechado Ganho", probabilityPercent: 100, category: "won", position: 5 },
    { id: "mock-s6", name: "Fechado Perdido", probabilityPercent: 0, category: "lost", position: 6 },
  ];
}

// ---------------------------------------------------------------------------
// Stages — adição
// ---------------------------------------------------------------------------

/**
 * Adiciona um estágio à BU.
 * POST /v1/business-units/{buId}/stages → 201 Created
 * Body: { name, probabilityPercent, category, position }
 */
export async function addStage(buId: string, input: AddStageInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.post<void>(`/business-units/${buId}/stages`, {
      name: input.name,
      probabilityPercent: input.probabilityPercent,
      category: input.category,
      position: input.position,
    });
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Stages — reordenação
// ---------------------------------------------------------------------------

/**
 * Reordena os estágios de pipeline de uma BU.
 * PUT /v1/business-units/{buId}/stages/{stageId} → 200
 * Body: { newPositions: Record<stageId, position> }
 *
 * O `stageId` na rota é ignorado pelo backend — o reorder é sempre completo
 * e todos os estágios devem aparecer no mapa `newPositions`.
 */
export async function reorderStages(buId: string, input: ReorderStagesInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.put<void>(
      `/business-units/${buId}/stages/reorder`,
      { newPositions: input.newPositions },
    );
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Stages — edição de atributos (nome, probabilidade, categoria)
// ---------------------------------------------------------------------------

/**
 * Edita os atributos de um estágio existente.
 * PUT /v1/business-units/{buId}/stages/{id} → 200
 * Body: { name, probabilityPercent, category }
 *
 * Distinto da reordenação (que altera apenas posições). Validações de domínio:
 * nome único (ORG-ERR-013), categoria/terminais (ORG-ERR-015), existência (ORG-ERR-016).
 */
export async function updateStage(buId: string, input: UpdateStageInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.put<void>(`/business-units/${buId}/stages/${input.stageId}`, {
      name: input.name,
      probabilityPercent: input.probabilityPercent,
      category: input.category,
    });
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Stages — remoção
// ---------------------------------------------------------------------------

/**
 * Remove um estágio da BU.
 * DELETE /v1/business-units/{buId}/stages/{id} → 204
 *
 * Retorna 409 se o estágio for o último estágio terminal da BU (ORG-ERR-015).
 */
export async function removeStage(buId: string, stageId: string): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.delete<void>(`/business-units/${buId}/stages/${stageId}`);
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Partnership Stages — listagem por BU
// ---------------------------------------------------------------------------

/**
 * Lista os estágios do pipeline de parcerias de uma BU, ordenados por posição.
 * GET /v1/business-units/{buId}/partnership-stages → StageResult[]
 *
 * Nota: este endpoint é autenticado corretamente (sem o bug do AllowAnonymous
 * presente em /stages). Erros são propagados normalmente.
 *
 * Seed default por BU: Prospecção/Diagnóstico/Modelagem/Negociação/Efetivado(won)/Recusado(lost).
 */
export async function listPartnershipStages(buId: string): Promise<readonly PortalStage[]> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    const dtos = await apiClient.get<StageApiDto[]>(`/business-units/${buId}/partnership-stages`);
    return dtos.map(mapApiStage);
  }

  // Mock: estágios padrão do pipeline de parceria para desenvolvimento
  return [
    { id: "mock-ps1", name: "Prospecção", probabilityPercent: 10, category: "open", position: 1 },
    { id: "mock-ps2", name: "Diagnóstico", probabilityPercent: 25, category: "open", position: 2 },
    { id: "mock-ps3", name: "Modelagem", probabilityPercent: 50, category: "open", position: 3 },
    { id: "mock-ps4", name: "Negociação", probabilityPercent: 75, category: "open", position: 4 },
    { id: "mock-ps5", name: "Efetivado", probabilityPercent: 100, category: "won", position: 5 },
    { id: "mock-ps6", name: "Recusado", probabilityPercent: 0, category: "lost", position: 6 },
  ];
}

// ---------------------------------------------------------------------------
// Partnership Stages — adição
// ---------------------------------------------------------------------------

/**
 * Adiciona um estágio ao pipeline de parcerias da BU.
 * POST /v1/business-units/{buId}/partnership-stages → 201 Created
 * Body: { name, probabilityPercent, category, position }
 */
export async function addPartnershipStage(buId: string, input: AddStageInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.post<void>(`/business-units/${buId}/partnership-stages`, {
      name: input.name,
      probabilityPercent: input.probabilityPercent,
      category: input.category,
      position: input.position,
    });
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Partnership Stages — reordenação
// ---------------------------------------------------------------------------

/**
 * Reordena os estágios do pipeline de parcerias de uma BU.
 * PUT /v1/business-units/{buId}/partnership-stages/reorder → 200
 * Body: { newPositions: Record<stageId, position> }
 */
export async function reorderPartnershipStages(buId: string, input: ReorderStagesInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.put<void>(
      `/business-units/${buId}/partnership-stages/reorder`,
      { newPositions: input.newPositions },
    );
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Partnership Stages — edição de atributos
// ---------------------------------------------------------------------------

/**
 * Edita os atributos de um estágio do pipeline de parcerias.
 * PUT /v1/business-units/{buId}/partnership-stages/{id} → 200
 * Body: { name, probabilityPercent, category }
 */
export async function updatePartnershipStage(buId: string, input: UpdateStageInput): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.put<void>(`/business-units/${buId}/partnership-stages/${input.stageId}`, {
      name: input.name,
      probabilityPercent: input.probabilityPercent,
      category: input.category,
    });
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Partnership Stages — remoção
// ---------------------------------------------------------------------------

/**
 * Remove um estágio do pipeline de parcerias da BU.
 * DELETE /v1/business-units/{buId}/partnership-stages/{id} → 204
 *
 * Retorna 409 se o estágio for o último estágio terminal (ORG-ERR-015).
 */
export async function removePartnershipStage(buId: string, stageId: string): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.delete<void>(`/business-units/${buId}/partnership-stages/${stageId}`);
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Tenant — leitura
// ---------------------------------------------------------------------------

/**
 * Obtém os dados do tenant corrente.
 * Área `tenants` real → GET /v1/tenant
 */
export async function getTenant(): Promise<PortalTenant> {
  const source = await resolveSource("tenants");

  if (source.kind === "api") {
    const dto = await apiClient.get<TenantApiDto>("/tenant");
    return mapApiTenant(dto);
  }

  if (!source.mock) throw new Error(`[repo] mock não disponível para área: ${source.area}`);
  const mock = source.mock;
  const t = mock.tenants[0];
  if (!t) {
    throw new Error("WEB-ERR: nenhum tenant disponível no mock");
  }
  return {
    id: t.id,
    slug: t.slug,
    displayName: t.name,
    timezone: t.tz,
    digestTime: "08:00",
    status: t.status,
  };
}

// ---------------------------------------------------------------------------
// Tenant — atualização
// ---------------------------------------------------------------------------

/**
 * Atualiza timezone e digestTime do tenant corrente.
 * PATCH /v1/tenant → TenantResponse
 */
export async function updateTenant(input: UpdateTenantInput): Promise<PortalTenant> {
  const source = await resolveSource("tenants");

  if (source.kind === "api") {
    const body: Record<string, string | undefined> = {};
    if (input.timezone) body["timezone"] = input.timezone;
    if (input.digestTime) body["digestTime"] = input.digestTime;
    const dto = await apiClient.patch<TenantApiDto>("/tenant", body);
    return mapApiTenant(dto);
  }

  return getTenant();
}

// ---------------------------------------------------------------------------
// Branding — leitura
// ---------------------------------------------------------------------------

/**
 * Obtém o branding do tenant corrente.
 * Área `branding` real → GET /v1/tenant/branding
 */
export async function getBranding(): Promise<PortalBranding> {
  const source = await resolveSource("branding");

  if (source.kind === "api") {
    const dto = await apiClient.get<BrandingApiDto>("/tenant/branding");
    return mapApiBranding(dto);
  }

  return {
    tenantId: "t-mock",
    logoUrl: null,
    faviconUrl: null,
    primaryColor: "#2563eb",
    secondaryColor: "#64748b",
    wcagContrastOk: true,
    lastContrastRatio: 8.59,
    updatedAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Branding — leitura pública (anônima), para os slots de identidade de TODO usuário
// ---------------------------------------------------------------------------

/**
 * Busca o branding PÚBLICO (anônimo) de um tenant via GET /brand/{slug}/brand.json — o mesmo
 * payload que a CDN cacheia e que a tela de login/marketing consome, sem exigir sessão de
 * TenantAdmin.
 *
 * Achado da revisão adversarial: o Shell alimentava os slots de identidade do tenant (sidebar,
 * seletor de tenant) e o BrandingApplier (cores globais) com getBranding()
 * (GET /v1/tenant/branding, [RequiresTenantAdmin]) — um Viewer (a maioria dos usuários do
 * produto) recebia 403 em toda página, `branding` ficava `null`, e o logo/cores do tenant nunca
 * apareciam para ele. Esta função é o substituto correto para QUALQUER slot que precise de
 * branding independente do papel do usuário logado; `getBranding()`/`useSaveBranding()`
 * continuam sendo o caminho certo só para a TELA de edição (PlatformPage), que já exige
 * TenantAdmin.
 *
 * Rota FORA de /v1 (mesma superfície pública same-origin do brand.json) — usa `fetch` direto em
 * vez de `apiClient`, que sempre prefixa `/v1`. Devolve `null` em qualquer falha (tenant
 * suspenso/inexistente, rede, 5xx) — o chamador trata como "sem branding" (fallback de iniciais),
 * nunca como erro fatal: este é um slot decorativo, não dado essencial da tela.
 */
export async function getPublicBrandJson(slug: string): Promise<PortalPublicBranding | null> {
  const source = await resolveSource("branding");

  if (source.kind === "api") {
    try {
      const response = await fetch(`/brand/${encodeURIComponent(slug)}/brand.json`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;

      // A rota /brand só existe no Ingress a partir de platform/k8s/gateway/gateway.yaml. Enquanto
      // ela não estiver viva, o catch-all do portal responde **200 com o index.html da SPA** —
      // medido em produção em 2026-09-07: `curl https://vellus.azim.consilium.tec.br/brand/vellus/
      // brand.json` devolve HTTP 200, content-type text/html, 1489 bytes. Sem esta checagem,
      // `response.ok` é verdadeiro, o `json()` estoura, o catch devolve null e as cores do tenant
      // — que HOJE funcionam — desapareceriam em silêncio. Trocar um defeito por dois.
      //
      // O content-type é o discriminante, e o erro é LANÇADO em vez de virar null: quem chama
      // precisa distinguir "este tenant não tem branding" de "a rota não está publicada".
      const contentType = response.headers.get("content-type") ?? "";
      if (!contentType.includes("application/json")) {
        throw new Error(
          `/brand/${slug}/brand.json respondeu ${response.status} com content-type "${contentType}" ` +
            "— a rota provavelmente não está no Ingress e o catch-all da SPA respondeu. " +
            "Aplique platform/k8s/gateway/gateway.yaml antes de promover o portal.",
        );
      }

      const dto = (await response.json()) as PublicBrandJsonApiDto;
      return {
        logoUrl: dto.logoUrl ?? null,
        faviconUrl: dto.faviconUrl ?? null,
        primaryColor: dto.colors?.primary ?? null,
        secondaryColor: dto.colors?.secondary ?? null,
      };
    } catch (erro) {
      // `catch` que engole tudo é a forma mais barata de transformar "a rota não existe" em
      // "este tenant não tem branding". O erro de rota-não-publicada acima é RELANÇADO de
      // propósito, para que o chamador possa cair no caminho autenticado que funciona hoje em
      // vez de renderizar sem cor nenhuma. Qualquer outra falha (rede, JSON malformado) continua
      // degradando para null, que é o contrato desta função.
      if (erro instanceof Error && erro.message.includes("não está no Ingress")) throw erro;
      return null;
    }
  }

  // Mock: mesmo fallback de cor que getBranding() usa em modo mock.
  return {
    logoUrl: null,
    faviconUrl: null,
    primaryColor: "#2563eb",
    secondaryColor: "#64748b",
  };
}

// ---------------------------------------------------------------------------
// Branding — atualização
// ---------------------------------------------------------------------------

/**
 * Atualiza o branding do tenant corrente.
 * PUT /v1/tenant/branding (multipart/form-data)
 * Campos: primaryColor, secondaryColor, logo? (File — PNG/SVG/JPEG/WebP), favicon? (idem + ICO)
 *
 * Usa `postMultipart` do ApiClient (exceção pontual de escopo autorizada) para
 * enviar FormData com Authorization e sem Content-Type (browser define boundary).
 *
 * Logo e favicon vão no MESMO FormData do PUT — uma única requisição atômica, porque
 * `TenantController.UpdateBranding` já aceita `logo`/`favicon` (`IFormFile?`) nesse endpoint e
 * `UpdateBrandingHandler` já faz upload + persistência de cores na mesma transação (fix mesclado
 * de `develop`, PR #162). A versão anterior chamava `uploadLogo()` (POST .../branding/logo,
 * endpoint separado) ANTES do PUT de cores — duas escritas não transacionais, com um modo de
 * falha real: se o PUT de cores rejeitasse por contraste insuficiente (TA-ERR-014) depois do
 * logo já ter sido gravado, o logo ficava órfão do lado do usuário (a tela mostrava erro, mas o
 * binário e a URL já tinham mudado no backend). Uma requisição só elimina essa classe de estado
 * parcial: ou os três campos são aceitos juntos, ou nenhum é — o favicon (REQ-16, DD-013) segue
 * a mesma regra.
 *
 * Retorno do backend: { wcagContrastOk, contrastRatio, logoUrl, faviconUrl, derivedTones? }
 * Recarrega o branding completo após o PUT para obter o shape PortalBranding.
 */
export async function updateBranding(input: UpdateBrandingInput): Promise<PortalBranding> {
  const source = await resolveSource("branding");

  if (source.kind === "api") {
    const formData = new FormData();
    formData.append("primaryColor", input.primaryColor);
    formData.append("secondaryColor", input.secondaryColor);
    if (input.logoFile instanceof File) {
      formData.append("logo", input.logoFile, input.logoFile.name);
    }
    if (input.faviconFile instanceof File) {
      formData.append("favicon", input.faviconFile, input.faviconFile.name);
    }
    await postMultipart<BrandingUpdateApiDto>("/tenant/branding", "PUT", formData);

    // Recarrega o branding completo para obter todos os campos (incluindo derivedTones).
    //
    // Achado da revisão adversarial: até aqui, uma falha SÓ nesta releitura (timeout, blip de
    // rede) rejeitava a função inteira — mesmo com o upload do logo e o PUT das cores já
    // confirmados pelo servidor. `BrandingSection` mostraria "não foi possível salvar" sobre uma
    // gravação que, de fato, aconteceu, convidando a um reenvio desnecessário. A escrita já está
    // feita neste ponto; o que falhou é só a confirmação. `PlatformPage.handleSaveBranding`
    // também refaz o `brandingQuery` logo depois de chamar esta função — é essa segunda consulta,
    // não o valor de retorno daqui, que sincroniza a tela com o servidor no caminho feliz.
    try {
      return await getBranding();
    } catch {
      return {
        // O id do tenant não está disponível aqui sem uma chamada de rede que ela mesma
        // acabou de falhar — a página não lê este campo do retorno (ver comentário acima),
        // só o consome para satisfazer o tipo de retorno.
        tenantId: "",
        logoUrl: null,
        faviconUrl: null,
        primaryColor: input.primaryColor,
        secondaryColor: input.secondaryColor,
        wcagContrastOk: true,
        lastContrastRatio: 0,
        updatedAt: new Date().toISOString(),
      };
    }
  }

  // Mock: retorna com as cores fornecidas
  return {
    tenantId: "t-mock",
    logoUrl: input.logoUrl ?? null,
    faviconUrl: input.faviconFile instanceof File ? URL.createObjectURL(input.faviconFile) : (input.faviconUrl ?? null),
    primaryColor: input.primaryColor,
    secondaryColor: input.secondaryColor,
    wcagContrastOk: true,
    lastContrastRatio: 4.5,
    updatedAt: new Date().toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Branding — upload de logo (separado do updateBranding para uso independente)
// ---------------------------------------------------------------------------

/**
 * Faz upload de logo para o branding do tenant.
 *
 * Endpoint dedicado: POST /v1/tenant/branding/logo (multipart, campo "logo").
 * O backend (tenant-administration) grava no storage e persiste a logoUrl no
 * branding, retornando { logoUrl }. Aceita PNG/SVG/JPEG/WebP (favicon também
 * aceita ICO), máx 2 MB. Retorna a URL pública do logo ou null em modo mock.
 *
 * NÃO é o caminho usado pela tela de Branding (ver `updateBranding` acima, que
 * anexa `logoFile` ao MESMO PUT de cores) — este função continua exportada para
 * uso independente (upload de logo sem alterar cores), mas hoje não tem
 * consumidor na UI.
 */
export async function uploadLogo(file: File): Promise<string | null> {
  const source = await resolveSource("branding");

  if (source.kind === "api") {
    const formData = new FormData();
    formData.append("logo", file, file.name);
    const result = await postMultipart<{ logoUrl?: string }>(
      "/tenant/branding/logo",
      "POST",
      formData,
    );
    return result?.logoUrl ?? null;
  }

  // Mock: simula URL local
  return URL.createObjectURL(file);
}

// ---------------------------------------------------------------------------
// Operações de plataforma (PlatformOperator)
// ---------------------------------------------------------------------------

/**
 * Provisiona um novo tenant na plataforma.
 * POST /v1/platform/tenants → ProvisionTenantResponse
 */
export async function provisionTenant(input: ProvisionTenantInput): Promise<ProvisionTenantResult> {
  const dto = await apiClient.post<ProvisionTenantApiDto>("/platform/tenants", {
    slug: input.slug,
    slugConfirmation: input.slugConfirmation,
    displayName: input.displayName,
    timezone: input.timezone,
    digestTime: input.digestTime,
    adminEmail: input.adminEmail,
  });
  return {
    tenantId: dto.tenantId,
    slug: dto.slug,
    status: dto.status,
  };
}

/**
 * Suspende um tenant ativo.
 * POST /v1/platform/tenants/{id}/suspend → TenantStateResponse
 */
export async function suspendTenant(tenantId: string): Promise<TenantStateResult> {
  const dto = await apiClient.post<TenantStateApiDto>(
    `/platform/tenants/${tenantId}/suspend`,
    {},
  );
  return { tenantId: dto.tenantId, status: dto.status };
}

/**
 * Reativa um tenant suspenso.
 * POST /v1/platform/tenants/{id}/reactivate → TenantStateResponse
 */
export async function reactivateTenant(tenantId: string): Promise<TenantStateResult> {
  const dto = await apiClient.post<TenantStateApiDto>(
    `/platform/tenants/${tenantId}/reactivate`,
    {},
  );
  return { tenantId: dto.tenantId, status: dto.status };
}

// ---------------------------------------------------------------------------
// Memberships (vínculo usuário ↔ BU ↔ papel) — Organization MembershipsController
// ---------------------------------------------------------------------------

/** Papéis válidos de membership (Organization service). */
export type MembershipRole = "tenant_admin" | "bu_manager" | "sales_ae" | "viewer";

/** Rótulos pt-BR dos papéis, para exibição na UI. */
export const MEMBERSHIP_ROLE_LABELS: Readonly<Record<MembershipRole, string>> = {
  tenant_admin: "Administrador",
  bu_manager: "Gestor da unidade de negócio",
  sales_ae: "Vendedor",
  viewer: "Visualizador",
};

/** Membership de um usuário em uma BU. */
export interface PortalMembership {
  readonly buId: string;
  readonly role: string;
}

/** Usuário do tenant com seus vínculos. GET /v1/users */
export interface PortalUser {
  readonly id: string;
  readonly email: string;
  /** Nome de exibição (pode ser vazio; consumidores caem para o email). */
  readonly displayName: string;
  readonly memberships: readonly PortalMembership[];
}

/** Membro de uma BU (projeção por BU, derivada de PortalUser). */
export interface PortalBuMember {
  readonly userId: string;
  readonly email: string;
  /** Nome de exibição (pode ser vazio; consumidores caem para o email). */
  readonly displayName: string;
  readonly role: string;
}

/** Canal de origem de uma BU. GET /v1/business-units/{buId}/origin-channels */
export interface PortalOriginChannel {
  readonly id: string;
  readonly name: string;
}

/** Entrada de criação de canal de origem. POST /v1/business-units/{buId}/origin-channels */
export interface AddOriginChannelInput {
  readonly name: string;
}

/**
 * Canal de origem na visão ADMINISTRATIVA, que enxerga os desativados.
 * GET /v1/business-units/{buId}/origin-channels/all
 */
export interface PortalOriginChannelAdmin {
  readonly id: string;
  readonly name: string;
  readonly active: boolean;
}

interface UserApiDto {
  readonly id: string;
  readonly email: string;
  readonly displayName?: string;
  readonly memberships?: ReadonlyArray<{ readonly buId: string; readonly role: string }>;
}

/**
 * Lista os usuários do tenant com seus memberships.
 * GET /v1/users → UserApiDto[]
 */
export async function listUsers(): Promise<readonly PortalUser[]> {
  const dtos = await apiClient.get<UserApiDto[]>("/users");
  return dtos.map((d) => ({
    id: d.id,
    email: d.email,
    displayName: d.displayName ?? "",
    memberships: (d.memberships ?? []).map((m) => ({ buId: m.buId, role: m.role })),
  }));
}

/**
 * Lista os membros vinculados a uma BU (filtro client-side sobre GET /v1/users).
 * O backend não expõe endpoint dedicado de membros por BU; derivamos dos memberships.
 */
export async function listBuMembers(buId: string): Promise<readonly PortalBuMember[]> {
  const users = await listUsers();
  const members: PortalBuMember[] = [];
  for (const user of users) {
    const membership = user.memberships.find((m) => m.buId === buId);
    if (membership) {
      members.push({
        userId: user.id,
        email: user.email,
        displayName: user.displayName,
        role: membership.role,
      });
    }
  }
  return members;
}

/** DTO de canal de origem retornado por GET /v1/business-units/{buId}/origin-channels. */
interface OriginChannelApiDto {
  readonly id: string;
  readonly name: string;
}

/**
 * Lista os canais de origem de uma BU.
 * GET /v1/business-units/{buId}/origin-channels → OriginChannelApiDto[]
 */
export async function listOriginChannels(
  buId: string,
): Promise<readonly PortalOriginChannel[]> {
  const source = await resolveSource("businessUnits");
  if (source.kind === "api") {
    const dtos = await apiClient.get<OriginChannelApiDto[]>(
      `/business-units/${buId}/origin-channels`,
    );
    return dtos.map((d) => ({ id: d.id, name: d.name }));
  }
  // Mock: canais padrão para desenvolvimento.
  return [
    { id: "mock-ch1", name: "Indicação Parceiro" },
    { id: "mock-ch2", name: "Outbound Direto" },
  ];
}

// ---------------------------------------------------------------------------
// Origin Channels — adição
// ---------------------------------------------------------------------------

/**
 * Adiciona um canal de origem à BU.
 * POST /v1/business-units/{buId}/origin-channels → 201 Created
 * Body: { name }
 */
export async function addOriginChannel(
  buId: string,
  input: AddOriginChannelInput,
): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.post<void>(`/business-units/${buId}/origin-channels`, {
      name: input.name,
    });
    return;
  }

  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Origin Channels — listagem administrativa (inclui desativados)
// ---------------------------------------------------------------------------

/**
 * Lista TODOS os canais da BU, ativos e desativados.
 * GET /v1/business-units/{buId}/origin-channels/all → OriginChannelAdminResult[]
 *
 * Rota separada da pública porque a pública é anônima (serve o opportunity-pipeline)
 * e enumerar configuração desativada do tenant sem autenticação seria vazamento.
 */
export async function listOriginChannelsAdmin(
  buId: string,
): Promise<readonly PortalOriginChannelAdmin[]> {
  const source = await resolveSource("businessUnits");
  if (source.kind === "api") {
    const dtos = await apiClient.get<Array<{ id: string; name: string; active: boolean }>>(
      `/business-units/${buId}/origin-channels/all`,
    );
    return dtos.map((d) => ({ id: d.id, name: d.name, active: d.active }));
  }
  // Mock: um desativado, para o caminho de reativação existir em desenvolvimento.
  return [
    { id: "mock-ch1", name: "Indicação Parceiro", active: true },
    { id: "mock-ch2", name: "Outbound Direto", active: true },
    { id: "mock-ch3", name: "Feira do Setor", active: false },
  ];
}

// ---------------------------------------------------------------------------
// Origin Channels — reativação
// ---------------------------------------------------------------------------

/**
 * Devolve ao uso um canal desativado, preservando id e histórico.
 * POST /v1/business-units/{buId}/origin-channels/{id}/reactivate → 200
 */
export async function reactivateOriginChannel(
  buId: string,
  channelId: string,
): Promise<void> {
  const source = await resolveSource("businessUnits");
  if (source.kind === "api") {
    await apiClient.post<void>(
      `/business-units/${buId}/origin-channels/${channelId}/reactivate`,
      {},
    );
    return;
  }
  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Origin Channels — renomear
// ---------------------------------------------------------------------------

/**
 * Renomeia um canal de origem, preservando id, estado e o vínculo das oportunidades
 * que já o registraram.
 * PUT /v1/business-units/{buId}/origin-channels/{id}/name → 200
 *
 * A rota termina em `/name` porque `PUT {id}` já significa DESATIVAR neste recurso —
 * reaproveitar o verbo faria a renomeação desligar o canal.
 */
export async function renameOriginChannel(
  buId: string,
  channelId: string,
  name: string,
): Promise<void> {
  const source = await resolveSource("businessUnits");
  if (source.kind === "api") {
    await apiClient.put<void>(
      `/business-units/${buId}/origin-channels/${channelId}/name`,
      { name },
    );
    return;
  }
  // Mock: no-op
}

// ---------------------------------------------------------------------------
// Origin Channels — desativação
// ---------------------------------------------------------------------------

/**
 * Desativa um canal de origem da BU (soft delete — ORG-ERR-016 se inexistente).
 * PUT /v1/business-units/{buId}/origin-channels/{id} → 200
 *
 * A desativação é definitiva pela API atual: canais desativados somem da
 * listagem (o backend filtra por `Active`) e não há endpoint de reativação.
 * Oportunidades já criadas preservam a referência ao canal.
 */
export async function deactivateOriginChannel(
  buId: string,
  channelId: string,
): Promise<void> {
  const source = await resolveSource("businessUnits");

  if (source.kind === "api") {
    await apiClient.put<void>(
      `/business-units/${buId}/origin-channels/${channelId}`,
      {},
    );
    return;
  }

  // Mock: no-op
}

/**
 * Vincula um usuário a uma BU com um papel.
 * POST /v1/users/{userId}/memberships → 201
 * Body: { buId, role }
 */
export async function assignMembership(userId: string, buId: string, role: MembershipRole): Promise<void> {
  await apiClient.post<void>(`/users/${userId}/memberships`, { buId, role });
}

/**
 * Altera o papel de um membership existente.
 * PUT /v1/users/{userId}/memberships/{buId} → 200
 * Body: { role }
 */
export async function changeMembershipRole(userId: string, buId: string, role: MembershipRole): Promise<void> {
  await apiClient.put<void>(`/users/${userId}/memberships/${buId}`, { role });
}

/**
 * Desvincula um usuário de uma BU.
 * DELETE /v1/users/{userId}/memberships/{buId} → 204
 */
export async function removeMembership(userId: string, buId: string): Promise<void> {
  await apiClient.delete<void>(`/users/${userId}/memberships/${buId}`);
}
